import { useCallback, useRef, useState } from "react";
import {
	type ColumnInput,
	createExportJob,
	pollExportDownload,
} from "../api/services/exportService";
import { downloadBlob } from "../lib/download";

const MAX_RETRIES = 15;
const INITIAL_DELAY_MS = 3000;
const MAX_DELAY_MS = 15000;

interface UseExportToExcelOptions {
	form: string;
	getColumns: () => ColumnInput[];
	getFilter?: () => Record<string, unknown> | undefined;
	fileName: string;
	onFinished?: () => void;
	onError?: (message: string) => void;
}

interface UseExportToExcelReturn {
	triggerExport: () => void;
	isExporting: boolean;
	downloadHasFailed: boolean;
	message: string;
}

export function useExportToExcel(
	options: UseExportToExcelOptions,
): UseExportToExcelReturn {
	const [isExporting, setIsExporting] = useState(false);
	const [downloadHasFailed, setDownloadHasFailed] = useState(false);
	const [message, setMessage] = useState("");
	const abortRef = useRef(false);

	const triggerExport = useCallback(() => {
		if (isExporting) return;
		abortRef.current = false;
		setIsExporting(true);
		setDownloadHasFailed(false);
		setMessage("Starting export...");

		const run = async () => {
			try {
				const filter = options.getFilter?.();
				const result = await createExportJob({
					format: "Xlsx",
					form: options.form,
					columns: options.getColumns(),
					filter: filter ? JSON.stringify(filter) : null,
				});

				const exportId = result.id;
				setMessage("Export queued, waiting for file...");

				for (let retry = 1; retry <= MAX_RETRIES; retry++) {
					if (abortRef.current) return;

					const delay = Math.min(
						INITIAL_DELAY_MS * retry,
						MAX_DELAY_MS,
					);
					await new Promise((resolve) => setTimeout(resolve, delay));

					if (abortRef.current) return;

					const pollResult = await pollExportDownload(exportId);

					if (pollResult.status === "ready") {
						const today = new Date().toISOString().slice(0, 10);
						downloadBlob(
							pollResult.blob,
							`${options.fileName}-${today}.xlsx`,
						);
						setIsExporting(false);
						setMessage("Export complete");
						options.onFinished?.();
						return;
					}

					if (pollResult.status === "failed") {
						setIsExporting(false);
						setDownloadHasFailed(true);
						setMessage(pollResult.message);
						options.onError?.(pollResult.message);
						return;
					}

					setMessage(
						`Waiting for export... (attempt ${retry}/${MAX_RETRIES})`,
					);
				}

				setIsExporting(false);
				setDownloadHasFailed(true);
				const msg = "Export timed out. Please try again later.";
				setMessage(msg);
				options.onError?.(msg);
			} catch (error) {
				setIsExporting(false);
				setDownloadHasFailed(true);
				const msg =
					error instanceof Error
						? error.message
						: "Export failed unexpectedly";
				setMessage(msg);
				options.onError?.(msg);
			}
		};

		run();
	}, [isExporting, options]);

	return { triggerExport, isExporting, downloadHasFailed, message };
}
